import { useCallback, useEffect, useRef } from 'react';

// Pause holds while either typing or hover is active; resume fires after both
// have been idle for IDLE_RESUME_MS. Tracks only its own intent, never the BE's
// state -- the BE force-resumes on its own 60s cap, and signal errors are
// swallowed, so worst case the countdown ticks while the user types.
export const IDLE_RESUME_MS = 5_000;

interface UseClarificationPauseDetectionArgs {
  /**
   * ``true`` when the chat input is routed to a pending clarification.
   * The hook only fires signals while true -- typing into general chat
   * must not pause clarifications for other parallel nodes.
   */
  isClarificationActive: boolean;
  /**
   * Fire-and-forget BE pause signal, injected by the host (which owns
   * the workflow/session/org lookup).  Called at most once per pause
   * epoch -- repeated keystrokes coalesce via an "already fired" ref.
   */
  onPauseSignal: () => Promise<void>;
  /**
   * Fire-and-forget BE resume signal.  Fired when (a) the input is empty
   * and the card isn't hovered for ``IDLE_RESUME_MS``, or (b) the active
   * clarification resolves.
   */
  onResumeSignal: () => Promise<void>;
}

interface UseClarificationPauseDetectionResult {
  /** Wire to ``Chat.onInputChange``.  Drives auto-pause-on-typing. */
  onInputChange: (value: string) => void;
  /** Wire to the card root's ``onMouseEnter`` / ``onMouseLeave`` (true
   *  on enter, false on leave).  Pauses the BE countdown while hovering,
   *  capturing "reading the question" intent before typing.  Multiple
   *  cards can fire concurrently; aggregation lives in the host. */
  setCardHoverState: (hovering: boolean) => void;
  /** Reset all pause state (when the active clarification resolves so
   *  the next pending one starts fresh). */
  reset: () => void;
  // Call when the BE force-resumes on its own: without clearing the flag the
  // next keystroke no-ops and the countdown keeps ticking. Idempotent.
  notifyExternalResume: () => void;
}

export const useClarificationPauseDetection = ({
  isClarificationActive,
  onPauseSignal,
  onResumeSignal,
}: UseClarificationPauseDetectionArgs): UseClarificationPauseDetectionResult => {
  // "Have we already fired a pause the BE committed to?" -- prevents
  // re-firing on every keystroke.  Reset on resume / resolve.
  const fePauseFiredRef = useRef(false);
  // Last typed value -- used so the idle-resume timer fires only after
  // genuine inactivity, not after a brief blur/focus cycle.
  const lastValueRef = useRef('');
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // A ref, not state: only the pause/resume decision reads it, and the
  // idle-resume timer must see the live value when it fires.
  const hoverActiveRef = useRef(false);

  // Stable refs to the injected signal-fires so the useCallbacks below
  // don't re-create on every host render.
  const pauseSignalRef = useRef(onPauseSignal);
  const resumeSignalRef = useRef(onResumeSignal);
  useEffect(() => {
    pauseSignalRef.current = onPauseSignal;
    resumeSignalRef.current = onResumeSignal;
  }, [onPauseSignal, onResumeSignal]);

  const firePause = useCallback(() => {
    if (fePauseFiredRef.current) return;
    fePauseFiredRef.current = true;
    void pauseSignalRef.current().catch(() => {
      // Sentry capture inside the service helper.  On failure we
      // clear the fired flag so a subsequent keystroke retries.
      fePauseFiredRef.current = false;
    });
  }, []);

  const fireResume = useCallback(() => {
    if (!fePauseFiredRef.current) return;
    fePauseFiredRef.current = false;
    void resumeSignalRef.current().catch(() => {
      // Sentry capture inside the service helper.  Don't restore the
      // fired flag -- the BE's view is now ambiguous, and the next
      // keystroke will re-fire pause anyway.
    });
  }, []);

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  // Schedule a resume after ``IDLE_RESUME_MS`` if both pause sources
  // (typing, hover) stay inactive.  Re-scheduled on every state-changing
  // event so a brief blip in one source doesn't trigger.
  const scheduleIdleResume = useCallback(() => {
    clearIdleTimer();
    if (hoverActiveRef.current) return;
    idleTimerRef.current = setTimeout(() => {
      idleTimerRef.current = null;
      // Refs, not closed-over values: a stale closure here would resume
      // against explicit user intent.
      if (lastValueRef.current.length === 0 && !hoverActiveRef.current) {
        fireResume();
      }
    }, IDLE_RESUME_MS);
  }, [clearIdleTimer, fireResume]);

  const onInputChange = useCallback(
    (value: string) => {
      lastValueRef.current = value;
      if (!isClarificationActive) return;
      if (value.length > 0) {
        // Active typing -- pause the BE timer and cancel any pending
        // idle-resume (``firePause`` coalesces re-fires).
        clearIdleTimer();
        firePause();
      } else {
        // Input cleared -- schedule an idle-resume so a brief
        // "delete and retype" cycle doesn't bounce pause off then on.
        scheduleIdleResume();
      }
    },
    [isClarificationActive, clearIdleTimer, firePause, scheduleIdleResume],
  );

  const setCardHoverState = useCallback(
    (hovering: boolean) => {
      // Gate on ``isClarificationActive`` so hover on resolved/historical
      // cards doesn't fire spurious signals (mirrors ``onInputChange``).
      // Skip the ref-write too so a stray hover can't leak a stale
      // ``true`` into the next clarification's ``scheduleIdleResume``.
      if (!isClarificationActive) return;
      hoverActiveRef.current = hovering;
      if (hovering) {
        // Mouse entered a pending card -- pause so the BE doesn't tick
        // while the user reads.  Cancels any pending idle-resume.
        clearIdleTimer();
        firePause();
      } else {
        // Mouse left -- schedule a resume so a brief in-and-out doesn't
        // bounce pause off then on.  Resume only fires if typing is also
        // inactive at fire time (see ``scheduleIdleResume``).
        scheduleIdleResume();
      }
    },
    [isClarificationActive, clearIdleTimer, firePause, scheduleIdleResume],
  );

  const reset = useCallback(() => {
    clearIdleTimer();
    fePauseFiredRef.current = false;
    lastValueRef.current = '';
    hoverActiveRef.current = false;
  }, [clearIdleTimer]);

  // See the result-interface JSDoc for the bug this guards.  Narrower
  // than ``reset``: only clears the "already fired pause" bookkeeping so
  // the next keystroke can re-pause, preserving ``lastValueRef`` and
  // hover intent (the force-resume means "safety cap", not "user changed
  // their mind").
  const notifyExternalResume = useCallback(() => {
    fePauseFiredRef.current = false;
  }, []);

  // Clean up the idle timer on unmount.
  useEffect(() => {
    return () => clearIdleTimer();
  }, [clearIdleTimer]);

  // When the active-clarification flag flips off, make sure we don't
  // leave the BE paused.
  useEffect(() => {
    if (!isClarificationActive) {
      clearIdleTimer();
      if (fePauseFiredRef.current) {
        fireResume();
      }
      lastValueRef.current = '';
      hoverActiveRef.current = false;
    }
  }, [isClarificationActive, clearIdleTimer, fireResume]);

  return {
    onInputChange,
    setCardHoverState,
    reset,
    notifyExternalResume,
  };
};
